/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';

import {IxModule} from '@siemens/ix-angular';
import {TranslateModule} from '@ngx-translate/core';
import {debounceTime, distinctUntilChanged, Subject} from 'rxjs';
import {AppSettings} from '../../../models/Constants';
import {ListBatteryObject} from '../../../models/ListBattery';
import {PassportService} from '../../../services/passport.service';
import {SearchCriteriaType} from '../../../models/SearchCriteria';

const MAX_PASSPORT_LIST_SIZE = 100;
/** Delay in ms after blur before closing the dropdown, to allow dropdown-item clicks to register first. */
const DROPDOWN_BLUR_CLOSE_DELAY_MS = 200;
let instanceCounter = 0;

@Component({
  selector: 'app-passport-search-select',
  imports: [IxModule, TranslateModule],
  templateUrl: './passport-search-select.component.html',
  styleUrl: './passport-search-select.component.scss',
})
export class PassportSearchSelectComponent implements OnInit, OnChanges {
  readonly passportService = inject(PassportService);

  /** Unique DOM id for this instance so ix-dropdown anchor resolves the correct element. */
  readonly inputId: string;

  @Output() itemClick: EventEmitter<ListBatteryObject> = new EventEmitter();
  @Input() label = '';
  @Input() excludedPassports: string[] = [];
  @Input() disabled = false;
  @Input() value = '';
  @Input() invalidText?: string = undefined;
  @Input() type?: SearchCriteriaType = undefined;

  loading = true;
  searchInput$ = new Subject<string>();
  filteredPassports: ListBatteryObject[] = [];
  dropdownVisible = signal(false);

  constructor() {
    instanceCounter++;
    this.inputId = `passport-search-select-${instanceCounter}`;
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['excludedPassports']) {
      await this.loadData();
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this.loadData().then(() => {
      this.loading = false;
    });
    this.searchInput$
      .pipe(
        debounceTime(AppSettings.INPUT_TO_REQUEST_WAIT_TIME_IN_MS),
        distinctUntilChanged(),
      )
      .subscribe((value) => this.onPassportSearch(value));
  }

  async loadData() {
    const initialPassportList = (
      await this.passportService.loadList({
        limit: MAX_PASSPORT_LIST_SIZE,
        type: this.type,
      })
    ).data;
    this.filteredPassports = this.filterPassports(initialPassportList);
  }

  /**
   * This function filters out the current passport, its ancestor, and all its descendants from the provided list of passports.
   * @param passports
   * @returns Filtered list of passports
   */
  filterPassports(passports: ListBatteryObject[]): ListBatteryObject[] {
    return Array.from(
      new Map(
        passports
          .filter((item) => !this.excludedPassports.includes(item.id))
          .map((item) => [item.id, item]),
      ).values(),
    );
  }

  async onPassportSearch(searchString: string) {
    this.loading = true;
    this.filteredPassports = await this.loadPassportsByNameAndId(searchString);
    this.loading = false;
  }

  async loadPassportsByNameAndId(
    searchString: string,
  ): Promise<ListBatteryObject[]> {
    const idPassports = (
      await this.passportService.loadList({
        limit: MAX_PASSPORT_LIST_SIZE,
        passportId: searchString,
        type: this.type,
      })
    ).data;
    const namePassports = (
      await this.passportService.loadList({
        limit: MAX_PASSPORT_LIST_SIZE,
        passportName: searchString,
        type: this.type,
      })
    ).data;
    const mergedPassports = [...idPassports, ...namePassports];
    return this.filterPassports(mergedPassports);
  }

  onPassportSearchInputChange(event: CustomEvent) {
    this.value = event.detail;
    this.searchInput$.next(event.detail);
  }

  async onInputFocus(): Promise<void> {
    if (this.value) {
      this.filteredPassports = await this.loadPassportsByNameAndId(this.value);
    } else {
      await this.loadData();
    }
    this.dropdownVisible.set(true);
  }

  onInputBlur(): void {
    // Small delay so a click on a dropdown item is processed before closing.
    setTimeout(
      () => this.dropdownVisible.set(false),
      DROPDOWN_BLUR_CLOSE_DELAY_MS,
    );
  }

  onPassportSelect(passport: ListBatteryObject) {
    this.value = '';
    this.itemClick.emit(passport);
  }
}
